import type { SimpleChanges } from '@angular/core';
import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { Router, RouterModule } from '@angular/router';

import { Store } from '@ngrx/store';
import type { Subject } from 'rxjs';
import { takeUntil } from 'rxjs';

import { provideAnalyticsViewContext } from '@core/analytics-legacy';
import { AnalyticsViewContext } from '@core/analytics-types';
import { collapse, filterBoolean, trackByIndex, trackByProp } from '@core/common-legacy';
import type { JobsIdMap } from '@core/offers-data';
import { JobsCombinedSelectors } from '@core/offers-data';
import type { OfferBrief } from '@core/offers-types';
import { OffersPageGroupHeadingComponent } from '@core/offers-ui';
import type { Requisition, RequisitionsParams } from '@core/requisitions-types';
import {
  BoxPlaceloaderComponent,
  IntersectionObserverEntryComponent,
} from '@core/user-interface-legacy';
import { LifeCycle } from '@core/utils-legacy';

import { RequisitionsListTileComponent } from '../../../components/list/requisitions-list-tile/requisitions-list-tile.component';
import { RequisitionsListFacade } from '../../../facades/list/requisitions-list.facade';
import type { RequisitionsGroupToDisplay } from '../../../requisitions.types';
import { RequisitionsDialogsService } from '../../../services/requisitions-dialogs.service';
import { RequisitionsListViewStateService } from '../../../services/requisitions-list-view-state.service';
import { RequisitionsSharedActionsService } from '../../../services/requisitions-shared-actions.service';
import { RequisitionsListEmptyStateContainerComponent } from '../requisitions-list-empty-state-container/requisitions-list-empty-state-container.component';

@Component({
  selector: 'rt-requisitions-list-tiles-view-container',
  templateUrl: './requisitions-list-tiles-view-container.component.html',
  styleUrls: ['./requisitions-list-tiles-view-container.component.less'],
  imports: [
    BoxPlaceloaderComponent,
    RequisitionsListEmptyStateContainerComponent,
    RequisitionsListTileComponent,
    RouterModule,
    IntersectionObserverEntryComponent,
    OffersPageGroupHeadingComponent,
  ],
  animations: [collapse],
  providers: [provideAnalyticsViewContext(AnalyticsViewContext.Requisitions)],
})
export class RequisitionsListTilesViewContainerComponent {
  public readonly placeholders: number[] = Array(5).fill(0);
  public readonly trackByIndex = trackByIndex;

  @Input() public requisitionsGroups: RequisitionsGroupToDisplay[] = [];
  @Input() public isLoading: boolean;
  @Input() public params: RequisitionsParams;
  @Input() public collapsedGroups: string[];
  @Input() public showGroups: boolean;

  public trackById = trackByProp<{ id: number }>({ prop: 'id' });
  public jobsMap: JobsIdMap;
  public showRequisitions: boolean;

  public canEditRequisitions: boolean;
  public canCreateJobs: boolean;

  @LifeCycle('onDestroy')
  private ngOnDestroy$: Subject<void>;

  constructor(
    private store: Store,
    private router: Router,
    private requisitionsDialogsService: RequisitionsDialogsService,
    private requisitionsListFacade: RequisitionsListFacade,
    private requisitionsSharedActionsService: RequisitionsSharedActionsService,
    private requisitionsViewState: RequisitionsListViewStateService,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.store
      .select(JobsCombinedSelectors.activeItemsAndArchivedBriefIdMap)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(jobsMap => {
        this.jobsMap = jobsMap;
        this.cdRef.detectChanges();
      });

    this.canEditRequisitions = this.requisitionsListFacade.canEditRequisitions;
    this.canCreateJobs = this.requisitionsListFacade.canCreateJobs;
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.showGroups || changes.requisitionsGroups) {
      this.handleRequisitionsVisibility();
    }
  }

  public follow(id: number): void {
    this.requisitionsSharedActionsService.follow(id);
  }

  public unfollow(id: number): void {
    this.requisitionsSharedActionsService.unfollow(id);
  }

  public createJob(id: number): void {
    this.requisitionsSharedActionsService.createJob(id);
  }

  public duplicate(id: number): void {
    this.requisitionsSharedActionsService.duplicate(id);
  }

  public delete(requisition: Requisition): void {
    this.requisitionsSharedActionsService.delete(requisition);
  }

  public archive(requisition: Requisition): void {
    this.requisitionsSharedActionsService.archive(requisition);
  }

  public retrieve(requisition: Requisition): void {
    this.requisitionsSharedActionsService.retrieve(requisition);
  }

  public cancelRequest(id: number): void {
    this.requisitionsSharedActionsService.cancelApprovalRequest(id);
  }

  public openRequisition(id: number): void {
    this.router.navigate(['requisitions', id]);
  }

  public showJobsListDialog(offers: OfferBrief[]): void {
    this.requisitionsDialogsService.openAssignedJobsDialog$(offers).subscribe();
  }

  public changeGroupVisibility(groupName: string): void {
    this.requisitionsViewState.setGroupVisibility(groupName);
  }

  public handleRequisitionsVisibility() {
    if (this.showGroups) {
      this.showRequisitions = !!this.requisitionsGroups.length;
    } else {
      const requisitions = this.requisitionsGroups[0]?.requisitions;
      this.showRequisitions = requisitions ? !!requisitions.length : true;
    }
  }
}
